import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import extractText from 'flarum/common/utils/extractText';
import { ARENA_ICONS } from '../../common';
import ModifierInfoModal from './ModifierInfoModal';
// Mirrors ArenaBattle::MODIFIERS (src/ArenaBattle.php) — kept as a plain
// hardcoded list here rather than fetched from the server, same pattern
// CardEditModal.tsx already uses for its fixed element-key list.
const MODIFIER_KEYS = ['classic', 'tournament', 'random_cards', 'friendly'];
// Mirrors ArenaBattle::DEFAULT_MAX_HP/DEFAULT_MAX_MANA (src/ArenaBattle.php)
// — the fixed values MODIFIER_TOURNAMENT pins both sides to, regardless of
// either player's personal max_hp/max_mana stat.
const TOURNAMENT_MAX_HP = 12;
const TOURNAMENT_MAX_MANA = 24;
export default class ChallengeModal extends Modal {
  username = '';
  loading = false;
  searching = false;
  error = null;
  suggestions = [];
  showSuggestions = false;
  searchTimer = null;
  loadingDecks = true;
  readyDeckSlots = [];
  selectedDeckSlotId = null;
  selectedModifier = 'classic';
  oninit(vnode) {
    super.oninit(vnode);
    if (this.attrs.opponent) {
      this.username = this.attrs.opponent.username() ?? '';
    }
    this.loadDeckSlots();
  }
  async loadDeckSlots() {
    this.loadingDecks = true;
    m.redraw();
    try {
      const response = await app.request({
        method: 'GET',
        url: `${app.forum.attribute('apiUrl')}/arena/deck-slots`
      });
      const slots = response?.slots ?? [];
      this.readyDeckSlots = slots.filter(s => s.cardCount === 20);
      this.selectedDeckSlotId = this.readyDeckSlots[0]?.id ?? null;
    } catch {
      this.readyDeckSlots = [];
      this.selectedDeckSlotId = null;
    } finally {
      this.loadingDecks = false;
      m.redraw();
    }
  }
  className() {
    return 'ArenaChallengeModal Modal--small';
  }
  title() {
    return m('[', null, m("i", {
      className: "fas fa-dungeon"
    }), " ", app.translator.trans('forumaker-arena.forum.challenge_modal.title'));
  }
  onremove() {
    if (this.searchTimer) clearTimeout(this.searchTimer);
  }
  onInput(value) {
    this.username = value;
    this.error = null;
    this.showSuggestions = false;
    if (this.searchTimer) clearTimeout(this.searchTimer);
    if (value.trim().length >= 3) {
      this.searching = true;
      m.redraw();
      this.searchTimer = setTimeout(() => this.searchUsers(value.trim()), 350);
    } else {
      this.suggestions = [];
      this.searching = false;
    }
  }
  async searchUsers(query) {
    try {
      const response = await app.request({
        method: 'GET',
        url: `${app.forum.attribute('apiUrl')}/users`,
        params: {
          'filter[q]': query,
          'page[limit]': 5
        }
      });
      const data = response?.data ?? [];
      this.suggestions = data.map(u => ({
        id: u.id,
        username: u.attributes?.username ?? '',
        displayName: u.attributes?.displayName ?? u.attributes?.username ?? '',
        avatarUrl: u.attributes?.avatarUrl ?? null
      })).filter(u => u.id !== app.session.user?.id());
      this.showSuggestions = this.suggestions.length > 0;
    } catch {
      this.suggestions = [];
      this.showSuggestions = false;
    }
    this.searching = false;
    m.redraw();
  }
  selectUser(user) {
    this.username = user.username;
    this.suggestions = [];
    this.showSuggestions = false;
    m.redraw();
  }
  content() {
    // Waiting out the deck-slots fetch behind a single spinner, instead of
    // rendering the full form immediately and letting the deck-select block
    // pop in a moment later, avoids the jarring reflow that used to shift
    // everything below it down once the request resolved.
    if (this.loadingDecks) {
      return m("div", {
        className: "Modal-body ArenaChallengeModal-loading"
      }, m(LoadingIndicator, null));
    }
    const hasDeck = this.readyDeckSlots.length > 0;

    // The actual battle is always created with the CHALLENGER's own max
    // HP/mana (see CreateBattleController::createBattleRecord(), which calls
    // ArenaBattle::getMaxHpForUser()/getMaxManaForUser() — these read the
    // per-user override in arena_stats, set either by an admin
    // (SetUserArenaOverrideController) or by a Battle Pass "hp"/"mana"
    // reward (BattlePassManager::grantReward())). This preview used to
    // always show the forum-wide default instead, so a player who'd earned
    // +1 max mana from the Pass still saw "24" here even though their next
    // battle would actually start at 25. arenaCurrentStats (see
    // Api\ArenaStatsUserFields) exposes that same resolved value for the
    // signed-in user, so read it first and only fall back to the forum
    // default if it's unavailable (e.g. arena.viewStats revoked).
    const currentStats = app.session.user?.attribute('arenaCurrentStats');
    const maxHp = currentStats?.maxHp ?? app.forum.attribute('arenaMaxHp') ?? 15;
    const maxMana = currentStats?.maxMana ?? app.forum.attribute('arenaMaxMana') ?? 20;
    const maxTurns = app.forum.attribute('arenaMaxTurns') ?? 10;

    // MODIFIER_TOURNAMENT disallows the "leave it blank, open a lobby"
    // path — an opponent must be picked up front, same as challenging from
    // a profile (see the disabled/hint logic below).
    const isTournament = this.selectedModifier === 'tournament';
    return m("div", {
      className: "Modal-body"
    }, this.error && m("div", {
      className: "Alert Alert--error",
      style: {
        marginBottom: '12px'
      }
    }, m("i", {
      className: "fas fa-exclamation-circle"
    }), " ", this.error), !hasDeck && m("div", {
      className: "Alert Alert--warning",
      style: {
        marginBottom: '12px'
      }
    }, m("i", {
      className: "fas fa-exclamation-triangle"
    }), ' ', app.translator.trans('forumaker-arena.forum.challenge_modal.no_deck'), ' ', m("a", {
      className: "ArenaChallengeModal-buildDeckLink",
      href: "#",
      onclick: e => {
        e.preventDefault();
        this.hide();
        m.route.set(app.route('user.arena', {
          username: app.session.user?.username() ?? ''
        }));
      }
    }, app.translator.trans('forumaker-arena.forum.challenge_modal.build_deck'))), m("div", {
      className: "ArenaChallengeModal-rules"
    }, m("div", {
      className: "ArenaChallengeModal-rule ArenaChallengeModal-rule--hp"
    }, m("i", {
      className: ARENA_ICONS.hp
    }), m("span", {
      className: "ArenaChallengeModal-ruleValue"
    }, isTournament ? TOURNAMENT_MAX_HP : maxHp), m("span", {
      className: "ArenaChallengeModal-ruleLabel"
    }, app.translator.trans('forumaker-arena.forum.challenge_modal.hp_label'))), m("div", {
      className: "ArenaChallengeModal-rule ArenaChallengeModal-rule--mana"
    }, m("i", {
      className: ARENA_ICONS.mana
    }), m("span", {
      className: "ArenaChallengeModal-ruleValue"
    }, isTournament ? TOURNAMENT_MAX_MANA : maxMana), m("span", {
      className: "ArenaChallengeModal-ruleLabel"
    }, app.translator.trans('forumaker-arena.forum.challenge_modal.mana_label'))), m("div", {
      className: "ArenaChallengeModal-rule ArenaChallengeModal-rule--turns"
    }, m("i", {
      className: "fas fa-list-ol"
    }), m("span", {
      className: "ArenaChallengeModal-ruleValue"
    }, maxTurns), m("span", {
      className: "ArenaChallengeModal-ruleLabel"
    }, app.translator.trans('forumaker-arena.forum.challenge_modal.turns_label', {
      maxTurns
    }))), m("div", {
      className: "ArenaChallengeModal-rule ArenaChallengeModal-rule--time"
    }, m("i", {
      className: "fas fa-clock"
    }), m("span", {
      className: "ArenaChallengeModal-ruleValue"
    }, app.translator.trans('forumaker-arena.forum.challenge_modal.turn_time_value')), m("span", {
      className: "ArenaChallengeModal-ruleLabel"
    }, app.translator.trans('forumaker-arena.forum.challenge_modal.per_turn_label')))), hasDeck && m("div", {
      className: "Form-group ArenaChallengeModal-deckWrap"
    }, m("label", null, app.translator.trans('forumaker-arena.forum.challenge_modal.deck_label')), m("select", {
      className: "FormControl",
      value: this.selectedDeckSlotId ?? '',
      disabled: this.selectedModifier === 'random_cards',
      onchange: e => {
        this.selectedDeckSlotId = parseInt(e.target.value, 10);
      }
    }, this.readyDeckSlots.map(slot => m("option", {
      key: slot.id,
      value: slot.id
    }, slot.name)))), m("div", {
      className: "Form-group ArenaChallengeModal-modifierWrap"
    }, m("label", null, app.translator.trans('forumaker-arena.forum.challenge_modal.modifier_label'), ' ', m("a", {
      href: "#",
      className: "ArenaChallengeModal-modifierInfo",
      "aria-label": extractText(app.translator.trans('forumaker-arena.forum.modifier_info.title')),
      onclick: e => {
        e.preventDefault();
        app.modal.show(ModifierInfoModal);
      }
    }, m("i", {
      className: "fas fa-question-circle"
    }))), m("select", {
      className: "FormControl",
      value: this.selectedModifier,
      onchange: e => {
        this.selectedModifier = e.target.value;
      }
    }, MODIFIER_KEYS.map(key => m("option", {
      key: key,
      value: key
    }, app.translator.trans(`forumaker-arena.lib.modifiers.${key}`))))), m("div", {
      className: "Form-group ArenaChallengeModal-searchWrap"
    }, m("label", null, app.translator.trans('forumaker-arena.forum.challenge_modal.opponent_label')), !this.attrs.opponent && m("p", {
      className: "ArenaChallengeModal-opponentHint helpText"
    }, isTournament ? app.translator.trans('forumaker-arena.forum.challenge_modal.opponent_hint_tournament') : app.translator.trans('forumaker-arena.forum.challenge_modal.opponent_hint')), m("div", {
      style: {
        position: 'relative'
      }
    }, m("input", {
      className: "FormControl",
      type: "text",
      placeholder: app.translator.trans('forumaker-arena.forum.challenge_modal.search_placeholder'),
      value: this.username,
      oninput: e => this.onInput(e.target.value),
      onkeydown: e => {
        if (e.key === 'Enter' && !this.showSuggestions) this.submit();
        if (e.key === 'Escape') {
          this.showSuggestions = false;
          m.redraw();
        }
      },
      disabled: this.loading
    }), this.searching && m("div", {
      className: "ArenaChallengeModal-searching"
    }, m("i", {
      className: "fas fa-circle-notch fa-spin"
    }), ' ', app.translator.trans('forumaker-arena.forum.challenge_modal.searching')), this.showSuggestions && m("ul", {
      className: "ArenaChallengeModal-suggestions"
    }, this.suggestions.map(u => m("li", {
      key: u.id,
      className: "ArenaChallengeModal-suggestion",
      onmousedown: e => {
        e.preventDefault();
        this.selectUser(u);
      }
    }, u.avatarUrl ? m("img", {
      className: "ArenaChallengeModal-avatar",
      src: u.avatarUrl,
      alt: ""
    }) : m("span", {
      className: "ArenaChallengeModal-avatarFallback"
    }, (u.displayName[0] ?? '?').toUpperCase()), m("span", {
      className: "ArenaChallengeModal-suggestionName"
    }, u.displayName), u.displayName !== u.username && m("span", {
      className: "ArenaChallengeModal-suggestionUsername"
    }, "@", u.username)))))), m("div", {
      className: "Form-group ArenaChallengeModal-submitGroup"
    }, m(Button, {
      className: "Button Button--primary Button--block",
      loading: this.loading,
      disabled: !hasDeck || this.selectedDeckSlotId == null || this.loading ||
      // A preset opponent (challenging from their profile) always
      // needs a name — only the generic entry point allows leaving
      // it blank to create an open lobby instead, and even that is
      // off the table for MODIFIER_TOURNAMENT (see isTournament
      // above).
      (!!this.attrs.opponent || isTournament) && !this.username.trim(),
      onclick: () => this.submit()
    }, m("i", {
      className: "fas fa-swords"
    }), " ", app.translator.trans('forumaker-arena.forum.challenge_modal.submit'))));
  }

  /**
   * A blank opponent field creates an open lobby anyone eligible can join
   * (see CreateBattleController::handleOpenBattle) instead of challenging a
   * named player — same single button either way, decided purely by whether
   * the field was left empty.
   */
  async submit() {
    const username = this.username.trim();
    if (this.selectedDeckSlotId == null) return;
    if (!username && this.attrs.opponent) return;
    this.loading = true;
    this.error = null;
    this.showSuggestions = false;
    m.redraw();
    if (!username) {
      return this.submitOpen();
    }
    try {
      const result = await app.request({
        method: 'POST',
        url: `${app.forum.attribute('apiUrl')}/arena/battles`,
        body: {
          opponentUsername: username,
          deckSlotId: this.selectedDeckSlotId,
          modifier: this.selectedModifier
        },
        // Suppress Flarum's default global error alert: our controller
        // replies with a custom JSON shape it can't render, and the modal
        // shows its own error banner anyway.
        errorHandler: () => {}
      });
      this.hide();
      m.route.set(result.discussionUrl);
    } catch (e) {
      // Flarum's RequestError puts the parsed JSON body directly on
      // e.response (not e.response.body) — our controller replies with a
      // custom JSON shape {error, battleId, discussionId}, not the standard
      // JSON:API {errors}.
      const body = e?.response ?? e?.response?.body ?? e?.responseJSON ?? {};

      // If a battle with this player already exists, redirect there instead
      if (body?.discussionId) {
        this.hide();
        m.route.set('/d/' + body.discussionId);
        return;
      }
      this.error = body?.error ?? body?.errors?.[0]?.detail ?? app.translator.trans('forumaker-arena.forum.challenge_modal.error_generic');
      this.loading = false;
      m.redraw();
    }
  }

  /**
   * Creates a battle with no opponent picked — anyone eligible can join it
   * afterward from the discussion (see JoinBattleController / ArenaBattleOpenPost).
   * Called from submit() above when the opponent field was left empty;
   * this.loading/this.error are already set by the caller.
   */
  async submitOpen() {
    try {
      const result = await app.request({
        method: 'POST',
        url: `${app.forum.attribute('apiUrl')}/arena/battles`,
        body: {
          open: true,
          deckSlotId: this.selectedDeckSlotId,
          modifier: this.selectedModifier
        },
        // Same global-alert suppression as submit() above.
        errorHandler: () => {}
      });
      this.hide();
      m.route.set(result.discussionUrl);
    } catch (e) {
      const body = e?.response ?? e?.response?.body ?? e?.responseJSON ?? {};
      if (body?.discussionId) {
        this.hide();
        m.route.set('/d/' + body.discussionId);
        return;
      }
      this.error = body?.error ?? body?.errors?.[0]?.detail ?? app.translator.trans('forumaker-arena.forum.challenge_modal.error_generic');
      this.loading = false;
      m.redraw();
    }
  }
}
flarum.reg.add('forumaker-arena', 'forum/components/ChallengeModal', ChallengeModal);